/**
 * Input Component
 * ===============
 * Covers: text inputs, email, password, search, textarea.
 * All variants share the same base styling from tokens.
 *
 * Accessibility:
 *  - Always use `label` prop (renders a visible <label>) or `aria-label`
 *  - Error messages are linked via aria-describedby
 *  - Required fields get aria-required
 */

import React from "react";
import { cn } from "@/theme/utils";

// ─── BASE INPUT ───────────────────────────────────────────────────────────────
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  /** Helper text shown below the input */
  hint?: string;
  /** Error message — shown in red, replaces hint */
  error?: string;
  /** Icon shown on the left inside the input */
  leftElement?: React.ReactNode;
  /** Icon or button shown on the right inside the input */
  rightElement?: React.ReactNode;
  inputSize?: "sm" | "md" | "lg";
}

const inputSizeStyles = {
  sm: "h-8  text-xs px-3",
  md: "h-10 text-sm px-3",
  lg: "h-12 text-sm px-4",
} as const;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      leftElement,
      rightElement,
      inputSize = "md",
      required,
      id: propId,
      className,
      ...props
    },
    ref
  ) => {
    // Generate stable ID if not provided
    const id = propId ?? `input-${props.name ?? Math.random().toString(36).slice(2, 8)}`;
    const errorId = `${id}-error`;
    const hintId  = `${id}-hint`;
    const hasError = Boolean(error);

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <label
            htmlFor={id}
            className="text-xs font-bold text-text-main tracking-wide"
          >
            {label}
            {required && (
              <span aria-hidden="true" className="text-error ml-1">*</span>
            )}
          </label>
        )}

        <div className="relative flex items-center">
          {leftElement && (
            <span
              aria-hidden="true"
              className="absolute left-3 flex items-center text-text-sub pointer-events-none"
            >
              {leftElement}
            </span>
          )}

          <input
            ref={ref}
            id={id}
            required={required}
            aria-required={required}
            aria-invalid={hasError}
            aria-describedby={
              [error ? errorId : null, hint ? hintId : null]
                .filter(Boolean)
                .join(" ") || undefined
            }
            className={cn(
              "w-full rounded-md border font-body bg-surface text-text-main",
              "placeholder:text-text-sub",
              "transition-[border-color,background-color]",
              "focus:outline-none focus:ring-0",
              "focus:border-primary focus:bg-surface-alt",
              hasError
                ? "border-error focus:border-error"
                : "border-border hover:border-border-mid",
              inputSizeStyles[inputSize],
              leftElement  && "pl-9",
              rightElement && "pr-9",
              className
            )}
            {...props}
          />

          {rightElement && (
            <span className="absolute right-3 flex items-center">
              {rightElement}
            </span>
          )}
        </div>

        {hasError && (
          <p id={errorId} role="alert" className="text-xs text-error font-medium">
            {error}
          </p>
        )}

        {!hasError && hint && (
          <p id={hintId} className="text-xs text-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

// ─── TEXTAREA ─────────────────────────────────────────────────────────────────
export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, required, id: propId, className, ...props }, ref) => {
    const id      = propId ?? `textarea-${props.name ?? Math.random().toString(36).slice(2, 8)}`;
    const errorId = `${id}-error`;
    const hasError = Boolean(error);

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <label htmlFor={id} className="text-xs font-bold text-text-main tracking-wide">
            {label}
            {required && <span aria-hidden="true" className="text-error ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={id}
          required={required}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          className={cn(
            "w-full rounded-md border font-body bg-surface text-text-main",
            "placeholder:text-text-sub resize-vertical min-h-[80px] p-3 text-sm",
            "transition-[border-color,background-color] leading-relaxed",
            "focus:outline-none focus:ring-0 focus:border-primary focus:bg-surface-alt",
            hasError
              ? "border-error focus:border-error"
              : "border-border hover:border-border-mid",
            className
          )}
          {...props}
        />

        {hasError && (
          <p id={errorId} role="alert" className="text-xs text-error font-medium">
            {error}
          </p>
        )}
        {!hasError && hint && (
          <p className="text-xs text-text-muted">{hint}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";
